import { AccentBox, LinkCard } from 'components/common';
import { ViewToggleOption } from 'components/core';
import { EntityOption } from 'components/core/ViewToggle/types';

import { Box, Flex, Icon, Tag, Typography } from '@project44/manifest-ds';
import { Skeleton } from '@project44/shared-components';

export interface IPermissions {
  authority: string;
}

export interface IAnalyticsPermissions {
  truckloadPermission?: boolean;
  oceanPermission?: boolean;
  portIntelPermission?: boolean;
  rootCausePermission?: boolean;
  oceanSustainability?: boolean;
}

export const skeletons = [1, 2, 3, 4].map((id) => (
  <Skeleton
    key={id}
    css={{
      flex: '1 1 0',
      height: '175px',
      maxWidth: '500px',
      minWidth: '300px',
    }}
  />
));

export const ANALYTICS_LINKS = (permissions?: IAnalyticsPermissions) => {
  const links = [
    permissions?.oceanPermission && {
      label: 'Ocean',
      tag: 'Ocean',
      description: 'View routes and carrier performance',
      link: '/analytics/ocean/summary',
    },
    permissions?.truckloadPermission && {
      label: 'Truckload',
      tag: 'Truckload',
      description: 'Monitor tracking quality and performance',
      link: '/analytics/truckload',
    },
    permissions?.portIntelPermission && {
      label: 'Port Intel',
      tag: 'Ocean',
      description: 'Monitor dwell time at ports globally',
      link: '/analytics/port-intel-v2',
    },
    permissions?.rootCausePermission && {
      label: 'Root Cause Analysis',
      tag: 'Truckload',
      description: 'Review tracking issues for truckload shipments',
      link: '/network/root-cause-analysis',
    },
    permissions?.oceanSustainability && {
      label: 'Ocean Sustainability',
      tag: 'Ocean',
      description: 'View global emissions and intensity',
      link: '/analytics/ocean-sustainability',
    },
  ].filter(Boolean);

  // TODO fix typescript with filter(boolean)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return links as any;
};

export const AnalyticsCards = (permissions: IAnalyticsPermissions) =>
  ANALYTICS_LINKS(permissions).map(
    ({
      description,
      label,
      link,
      tag,
    }: {
      description: string;
      label: string;
      link: string;
      tag: string;
    }) => (
      <LinkCard key={label} to={link} label={`View ${label}`}>
        <Box css={{ padding: '$medium' }}>
          <Flex
            align="center"
            justify="between"
            css={{ marginBottom: '$x-small' }}
          >
            <Typography variant="subtitle" as="h1">
              {label}
            </Typography>
            <Tag>{tag}</Tag>
          </Flex>
          <Typography
            variant="subtext"
            as="p"
            css={{ color: '$text-secondary' }}
          >
            {description}
          </Typography>
        </Box>
      </LinkCard>
    ),
  );

const CreateCard = (label: string) => (
  <LinkCard
    key={label}
    to={`/results?filters=(listView:all-${label})`}
    label="Create View"
  >
    <Flex
      wrap
      gap="x-small"
      align="center"
      orientation="vertical"
      css={{ padding: '$large $medium 0', height: '124px' }}
    >
      <Icon icon="add" css={{ fontSize: '$large' }} />
      <Typography variant="subtitle">Create a new view</Typography>
      <Typography
        variant="caption"
        css={{ textAlign: 'center', color: '$text-tertiary' }}
      >
        {`Add filters and create a view to quickly find ${label} that meet criteria`}
      </Typography>
    </Flex>
  </LinkCard>
);

const mapCard = (
  label: string,
  onShow: (key: string) => void,
  {
    id,
    accentColor,
    name,
    totalResults,
    createView,
    deleteView,
    updateView,
  }: EntityOption,
) => (
  <LinkCard
    key={id}
    to={`/results?filters=(listView:${id})`}
    label={`View ${label}`}
    onShow={onShow}
  >
    <ViewToggleOption
      accentColor={accentColor}
      disableDrag
      isEdit={true}
      isSelected={false}
      name={name}
      createView={createView}
      deleteView={deleteView}
      updateView={updateView}
    />
    <Flex gap="small" align="end" css={{ padding: '0 $medium $medium' }}>
      <Typography variant="display">
        {totalResults || totalResults === 0 ? totalResults : 'N/A'}
      </Typography>
      <Typography
        variant="body"
        css={{ color: '$text-secondary', lineHeight: '$x-large' }}
      >
        {label}
      </Typography>
    </Flex>
  </LinkCard>
);

export const mapDarkCard = ({
  accentColor,
  count,
  label,
  link,
  viewType,
}: {
  accentColor: string;
  count: number | string;
  label: string;
  link: string;
  viewType: string;
}) => (
  <LinkCard key={label} to={link} label={label} variant="dark">
    <Flex
      gap="small"
      align="center"
      css={{ color: '$text-contrast', padding: '$medium' }}
    >
      <AccentBox bgColor={accentColor} css={{ height: 38, width: 12 }} />
      <Flex gap="small" align="end" css={{ padding: '$small 0' }}>
        <Typography variant="display">{count}</Typography>
        <Typography
          variant="body"
          css={{ color: '$palette-grey-300', lineHeight: '$x-large' }}
        >
          total {viewType}
        </Typography>
      </Flex>
    </Flex>
  </LinkCard>
);

export const CreateShipmentCard = CreateCard('shipments');
export const CreateOrderCard = CreateCard('orders');
export const mapShipmentCard = (
  entity: EntityOption,
  onShow: (key: string) => void,
  getTotalResults: (key: string) => number | string,
) => {
  return mapCard('Shipments', onShow, {
    ...entity,
    totalResults: getTotalResults && getTotalResults(entity.id),
  });
};
export const mapOrderCard = (
  entity: EntityOption,
  onShow: (key: string) => void,
  getTotalResults: (key: string) => number | string,
) => {
  return mapCard('Orders', onShow, {
    ...entity,
    totalResults: getTotalResults && getTotalResults(entity.id),
  });
};

export const permissionsToCheck = {
  truckloadPermission: 'read:truckload_analytics',
  oceanPermission: 'VIEW_VOC2_OCEAN_ANALYTICS_DASHBOARD',
  portIntelPermission: 'VIEW_PORT_INTEL',
  rootCausePermission: 'VIEW_TL_TRACKING_REPORT',
  oceanSustainability: 'VIEW_OCEAN_SUSTAINABILITY',
};

export const checkPermission = (
  permissions?: IPermissions[],
  authority?: string,
) => {
  return permissions?.some((element) => element.authority === authority);
};
